import React, { useState, useEffect, useRef } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import useScoFormsEnabled from "../../hooks/useScoFormsEnabled";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeftLong } from "@fortawesome/pro-regular-svg-icons";
import toast from "react-hot-toast";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import Breadcrumbs from "../../components/Breadcrumbs";
import Dropdown from "../../components/Dropdown";
import InputField from "../../components/InputField";
import HardwareOrderSection from "../../components/hardware/HardwareOrderSection";
import CustomQuestionFields from "../../components/user/CustomQuestionFields";
import { useAuth } from "../../providers/AuthContext";
import { getBillingGroupsByAgreement } from "../../api/BillingGroups";
import { getScoCustomQuestions } from "../../api/Tenants";
import { getScoFormSettings } from "../../api/Users";
import { getHardwareAssortment } from "../../api/Hardware";
import { submitHardwareOrder } from "../../api/HardwareOrders";
import { EMPTY_HARDWARE_ORDER, validateHardware } from "../../components/hardware/hardwareFormData";

/**
 * Standalone hardware order (PBI #308): hardware for the customer without an onboarding. The
 * assortment follows the chosen billing group; names and prices are resolved by the API when it
 * builds the case mail, and the orderer gets the case reference back.
 */
const HardwareOrderPage = () => {
  const { agreementPublicId, tenantPublicId } = useParams();
  const { enabled, loaded } = useScoFormsEnabled(agreementPublicId);
  const { getAccessToken } = useAuth();
  const navigate = useNavigate();

  const [supported, setSupported] = useState(null);
  const [billingGroups, setBillingGroups] = useState([]);
  const [billingGroupPublicId, setBillingGroupPublicId] = useState("");
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [order, setOrder] = useState(EMPTY_HARDWARE_ORDER);
  const [comment, setComment] = useState("");
  const [customQuestions, setCustomQuestions] = useState([]);
  const [customAnswers, setCustomAnswers] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const accessToken = await getAccessToken();
        const [settings, groups, questions] = await Promise.all([
          getScoFormSettings(accessToken).catch(() => null),
          getBillingGroupsByAgreement(agreementPublicId, accessToken).catch(() => []),
          getScoCustomQuestions(tenantPublicId, accessToken).catch(() => []),
        ]);
        if (cancelled) return;
        setSupported(!!settings?.supportsStandaloneHardwareOrder);
        setBillingGroups(groups || []);
        setCustomQuestions(questions || []);
        // The assortment differs per billing group, so one is always chosen: the agreement's
        // default group, otherwise the first. The orderer can switch.
        const preselected = (groups || []).find((g) => g.isDefault) || (groups || [])[0];
        if (preselected) setBillingGroupPublicId(preselected.billingGroupPublicId);
      } catch (error) {
        if (cancelled) return;
        setSupported(false);
        toast.error("Could not load the order page");
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [agreementPublicId, tenantPublicId]);

  useEffect(() => {
    if (!supported) return;

    let cancelled = false;
    const load = async () => {
      setProductsLoading(true);
      try {
        const accessToken = await getAccessToken();
        const assortment = await getHardwareAssortment(agreementPublicId, billingGroupPublicId || null, accessToken);
        if (!cancelled) setProducts(assortment || []);
      } catch (error) {
        if (!cancelled) {
          setProducts([]);
          toast.error("Could not load the hardware assortment");
        }
      } finally {
        if (!cancelled) setProductsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [supported, agreementPublicId, billingGroupPublicId]);

  const selectedBillingGroup = billingGroups.find((g) => g.billingGroupPublicId === billingGroupPublicId);
  const hardwareError = validateHardware("new", order);

  const handleBillingGroupChange = (name) => {
    const group = billingGroups.find((g) => g.name === name);
    if (!group) return;
    setBillingGroupPublicId(group.billingGroupPublicId);
    // Chosen products may not exist in the new group's assortment.
    setOrder((prev) => ({ ...prev, items: {} }));
  };

  const handleSubmit = async () => {
    setSubmitAttempted(true);
    if (hardwareError) {
      toast.error(hardwareError);
      return;
    }
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsSubmitting(true);

    try {
      const accessToken = await getAccessToken();
      const payload = {
        source: "Dip",
        tenantPublicId,
        billingGroupPublicId: billingGroupPublicId || null,
        items: Object.entries(order.items)
          .filter(([, quantity]) => quantity > 0)
          .map(([hardwareProductPublicId, quantity]) => ({ hardwareProductPublicId, quantity })),
        desiredDeliveryDate: order.desiredDeliveryDate || null,
        onSiteHelp: !!order.onSiteHelp,
        onSiteHelpNotes: order.onSiteHelp ? order.onSiteHelpNotes || null : null,
        deliverTo: order.deliverTo || null,
        deliveryAddress: order.deliveryAddress || null,
        otherRequests: order.otherRequests || null,
        comment: comment || null,
        customQuestions: Object.keys(customAnswers).length > 0 ? customAnswers : null,
      };

      const result = await submitHardwareOrder(payload, accessToken);

      toast.success(`Order sent to Diflex. Case reference: ${result.caseReference}`, {
        duration: 12000,
        iconTheme: { primary: "#4CAF50", secondary: "#DFF2BF" },
      });
      navigate(-1);
    } catch (error) {
      const status = error?.response?.status;
      toast.error(
        status === 403
          ? "You are not allowed to order hardware for this customer"
          : "Failed to send the order"
      );
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  if (loaded && !enabled) {
    return <Navigate to={`/${agreementPublicId}/${tenantPublicId}/users`} replace />;
  }

  return (
    <>
      <HeaderBar />
      <div className="flex flex-row w-full">
        <Sidebar />
        <main className="page">
          <div className="page-content">
            <Breadcrumbs />
            <div className="flex flex-row items-center pb-4">
              <button onClick={() => navigate(-1)} className="text-black flex">
                <FontAwesomeIcon icon={faArrowLeftLong} className="w-4 pr-4" />
              </button>
              <h2 className="border-l border-mute-200 pl-4">Order hardware</h2>
            </div>

            <div className="flex flex-col px-4 py-8 shadow-lg border rounded-md w-full gap-4">
              {supported === false && (
                <p className="text-sm text-gray-600">
                  Hardware ordering is not available right now. Contact Diflex support to order hardware.
                </p>
              )}

              {supported && (
                <div className="grid grid-cols-2 gap-6 w-full">
                  <div className="flex flex-col gap-4">
                    <p className="text-sm text-gray-600">
                      Order hardware for your organisation without creating a user. Diflex confirms
                      the order by mail with the case reference shown after sending.
                    </p>

                    {billingGroups.length > 1 && (
                      <Dropdown
                        label="Billing group"
                        value={selectedBillingGroup?.name || ""}
                        options={billingGroups.map((g) => g.name)}
                        onSelect={handleBillingGroupChange}
                        placeholder="Choose billing group"
                        searchable={true}
                      />
                    )}

                    <HardwareOrderSection
                      products={products}
                      loading={productsLoading}
                      order={order}
                      onOrderChange={setOrder}
                    />
                    {submitAttempted && hardwareError && (
                      <p className="text-sm text-error-600">{hardwareError}</p>
                    )}
                  </div>

                  <div className="flex flex-col gap-4">
                    <InputField
                      label="Comment"
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      placeholder="Anything Diflex should know about this order"
                      type="text-area"
                    />

                    <CustomQuestionFields
                      questions={customQuestions}
                      answers={customAnswers}
                      onAnswerChange={(questionPublicId, value) =>
                        setCustomAnswers((prev) => ({ ...prev, [questionPublicId]: value }))
                      }
                    />
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-4 pt-4">
                <button className="btn btn-outline-primary" onClick={() => navigate(-1)}>
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  disabled={!supported || isSubmitting}
                  onClick={handleSubmit}
                >
                  {isSubmitting ? "Sending..." : "Send order"}
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default HardwareOrderPage;
